// ExtraContributionsEditor.tsx — "Sonderzahlungen" (Einmalige Sonderzahlungen feature):
// a compact add/remove row list inside the Sparplan section. Each row is a Monat/Jahr
// picker (<input type="month">) + a € amount; the conversion to
// ForecastConfig.extraContributions[].monthIndex/grossCents happens centrally at the API
// boundary (lib/scenario.ts's buildForecastConfig via lib/convert.ts's
// monthIndexFromYearMonth) — this component only edits the € / YYYY-MM form rows, same
// "floats stay on the form side" convention as every other field in ForecastFields.
//
// The amount € input is paired with a bare nice-steps range input (lib/niceSteps.ts,
// same "money" scale NumberField's `sliderScale="money"` uses) rather than a full
// NumberField — a NumberField's own label + stacked slider-row would roughly double
// each row's height, and this list can hold several rows. Reusing the plain MONEY_STEPS
// array/helpers directly keeps this row's existing compact layout while still giving the
// same "land on a round number without fighting the drag" benefit.
import type { ExtraContributionRow } from '../lib/scenario.js';
import { MONEY_STEPS, indexToValue, valueToNearestIndex } from '../lib/niceSteps.js';
import { Hint } from './Hint.js';

export interface ExtraContributionsEditorProps {
  startDate: string;
  rows: ExtraContributionRow[];
  onChange: (rows: ExtraContributionRow[]) => void;
}

function newRowId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `row-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function ExtraContributionsEditor({ startDate, rows, onChange }: ExtraContributionsEditorProps) {
  function addRow() {
    const defaultMonth = startDate.slice(0, 7); // "YYYY-MM" from the plan's own start date
    onChange([...rows, { id: newRowId(), yearMonth: defaultMonth, amountEur: 1000 }]);
  }
  function updateRow(id: string, patch: Partial<ExtraContributionRow>) {
    onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }
  function removeRow(id: string) {
    onChange(rows.filter((r) => r.id !== id));
  }

  return (
    <div class="field">
      <span class="field-label">Sonderzahlungen</span>
      {rows.length === 0 ? (
        <p class="field-hint">Keine Sonderzahlungen geplant.</p>
      ) : (
        <ul class="source-list">
          {rows.map((row, i) => (
            <li key={row.id} class="source-row extra-contribution-row">
              <input
                type="month"
                value={row.yearMonth}
                onInput={(e) => updateRow(row.id, { yearMonth: (e.currentTarget as HTMLInputElement).value })}
                aria-label={`Monat/Jahr der Sonderzahlung ${i + 1}`}
              />
              <input
                type="number"
                min={0}
                step={100}
                value={row.amountEur}
                onInput={(e) =>
                  updateRow(row.id, { amountEur: Number((e.currentTarget as HTMLInputElement).value) || 0 })
                }
                aria-label={`Betrag der Sonderzahlung ${i + 1} in Euro`}
              />
              <input
                type="range"
                class="extra-contribution-slider"
                min={0}
                max={MONEY_STEPS.length - 1}
                step={1}
                value={valueToNearestIndex(MONEY_STEPS, row.amountEur)}
                onInput={(e) =>
                  updateRow(row.id, {
                    amountEur: indexToValue(MONEY_STEPS, Number((e.currentTarget as HTMLInputElement).value)),
                  })
                }
                aria-label={`Betrag der Sonderzahlung ${i + 1} Schieberegler (rundet auf glatte Beträge)`}
              />
              <button
                type="button"
                class="btn"
                onClick={() => removeRow(row.id)}
                aria-label={`Sonderzahlung ${i + 1} entfernen`}
              >
                Entfernen
              </button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" class="btn" onClick={addRow} style={{ marginTop: '0.4rem' }}>
        + Sonderzahlung hinzufügen
      </button>
      <Hint>
        <p class="field-hint">
          Einmalige Zuzahlungen zusätzlich zur monatlichen Rate (z. B. Bonus oder Erbschaft).
          Es fällt dieselbe Ausführungsgebühr wie bei einer regulären Sparplan-Ausführung an
          (mehrere Sonderzahlungen im selben Monat teilen sich eine Gebühr). Nur während der
          Ansparphase wirksam, nicht während einer Entnahmephase.
        </p>
      </Hint>
    </div>
  );
}
